import React, { useState, useEffect } from 'react';
import { voiceService } from '../services/voiceService';
import { useIsMobile } from '../hooks/useIsMobile';

// Закрытые полосы помним между транскрибациями: одну и ту же запись прогоняют
// по нескольку раз, и отвечать про тех же людей на каждый прогон незачем.
// Ключ — имя записи, поэтому «та же запись» узнаётся, а другая встреча с теми
// же участниками полосу покажет.
const DISMISS_STORE = 'voiceIdBar.dismissed';
const DISMISS_LIMIT = 50;   // не даём списку расти бесконечно, старое вытесняется

const readDismissed = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(DISMISS_STORE));
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];   // приватный режим или битое значение — просто живём без памяти
  }
};

const rememberDismissed = (key) => {
  try {
    const list = readDismissed().filter((item) => item !== key);
    list.push(key);
    localStorage.setItem(DISMISS_STORE, JSON.stringify(list.slice(-DISMISS_LIMIT)));
  } catch (e) {
    /* не смогли запомнить — полоса просто появится снова, это не повод падать */
  }
};

/**
 * Полоса подтверждения узнанных участников.
 *
 * Появляется только когда узнавание по голосу кого-то подписало. «Это он»
 * дописывает эталон к отпечатку — это единственный дешёвый способ набрать
 * несколько записей на человека, а точность держится именно на среднем по
 * нескольким. «Не он» открывает переименование и отправляет отрицательный
 * сигнал: ложные срабатывания иначе нигде не видны, а без них порог не
 * откалибровать.
 *
 * recognized: [{ label, name, speakerId, confirmed }]
 *
 * `confirmed` приезжает из сохранённого состояния страницы: подтверждение
 * обязано пережить перезагрузку, иначе полоса спрашивает про одного и того же
 * человека снова и снова, а человек уверен, что уже ответил.
 *
 * `dismissKey` — имя записи. Крестик прячет полосу и запоминает её закрытой для
 * этой записи, чтобы повторный прогон того же файла не спрашивал заново.
 */
const VoiceIdBar = ({ recognized, taskId, onCorrect, onConfirmed,
                     suggested = [], onAccepted, dismissKey }) => {
  const [state, setState] = useState({});   // speakerId → 'saving' | 'done' | текст ошибки
  const [dismissed, setDismissed] = useState(false);
  // Телефон: полоса свёрнута в строку «Узнаны по голосу: имена ▾». Развёрнутой
  // она занимала полэкрана над расшифровкой (тест владельца на iPhone, 27.09).
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);

  // Ключ приезжает не сразу (имя записи появляется вместе с задачей), поэтому
  // сверяемся с памятью на каждое его изменение, а не один раз при монтировании.
  useEffect(() => {
    setDismissed(dismissKey ? readDismissed().includes(dismissKey) : false);
  }, [dismissKey]);

  if (dismissed) return null;
  if (!taskId || (!recognized?.length && !suggested.length)) return null;

  const dismiss = () => {
    setDismissed(true);
    if (dismissKey) rememberDismissed(dismissKey);
  };

  const confirm = async (item) => {
    setState((prev) => ({ ...prev, [item.speakerId]: 'saving' }));
    try {
      const result = await voiceService.remember(taskId, { [item.speakerId]: item.name });
      const refused = [
        ...(result?.skipped_short || []).map(() => 'мало речи в этой записи'),
        ...(result?.skipped_mismatch || []).map(() => 'голос не похож на сохранённый'),
        ...(result?.skipped_unavailable || []).map(() => 'данные записи уже недоступны'),
      ];
      const outcome = refused.length ? refused[0] : (result?.saved ? 'done' : 'уже сохранено');
      setState((prev) => ({
        // Отказ показываем словами, а не молчанием: человек нажал и ждёт ответа.
        ...prev, [item.speakerId]: outcome,
      }));
      // Успех запоминаем в состоянии страницы — оно переживает перезагрузку.
      if (!refused.length && onConfirmed) onConfirmed(item.speakerId);
    } catch (e) {
      setState((prev) => ({
        ...prev,
        [item.speakerId]: e.response?.data?.detail || e.message || 'не удалось сохранить',
      }));
    }
  };

  // Почти-совпадение: подтверждение и назовёт человека в расшифровке, и
  // добавит ему эталон — ровно того эталона и не хватало, чтобы в следующий раз
  // узнать уверенно.
  const accept = async (item) => {
    setState((prev) => ({ ...prev, [item.speakerId]: 'saving' }));
    try {
      const result = await voiceService.remember(taskId, { [item.speakerId]: item.name });
      const refused = [
        ...(result?.skipped_short || []).map(() => 'мало речи в этой записи'),
        ...(result?.skipped_mismatch || []).map(() => 'голос не похож на сохранённый'),
        ...(result?.skipped_unavailable || []).map(() => 'данные записи уже недоступны'),
      ];
      if (refused.length) {
        setState((prev) => ({ ...prev, [item.speakerId]: refused[0] }));
        return;
      }
      setState((prev) => ({ ...prev, [item.speakerId]: 'done' }));
      if (onAccepted) onAccepted(item);
    } catch (e) {
      setState((prev) => ({
        ...prev,
        [item.speakerId]: e.response?.data?.detail || e.message || 'не удалось сохранить',
      }));
    }
  };

  const declineSuggestion = (item) => {
    voiceService.reject(taskId, item.speakerId).catch(() => {});
    setState((prev) => ({ ...prev, [item.speakerId]: 'declined' }));
  };

  const reject = (item) => {
    // Отрицательный сигнал уходит фоном — человеку важно поскорее исправить имя.
    voiceService.reject(taskId, item.speakerId).catch(() => {});
    setState((prev) => ({ ...prev, [item.speakerId]: 'rejected' }));
    if (onCorrect) onCorrect(item.label);
  };

  const closeButton = (
    <button
      type="button"
      onClick={dismiss}
      aria-label="Скрыть полосу узнавания"
      title="Скрыть. Для этой записи больше не спросим"
      className="absolute top-1.5 right-2 w-6 h-6 flex items-center justify-center rounded text-lg leading-none text-paper-accent hover:text-paper-accent hover:bg-paper-panel transition-colors"
    >
      ×
    </button>
  );

  if (isMobile && !expanded) {
    const names = [...recognized, ...suggested].map((item) => item.name).join(', ');
    return (
      <div className="kbd-hide relative mb-2 rounded-panel bg-paper-bg text-sm">
        <button type="button" onClick={() => setExpanded(true)} aria-expanded={false}
                data-testid="voice-id-toggle"
                className="w-full min-h-11 flex items-center gap-2 pl-4 pr-10 text-left">
          <span className="shrink-0 text-paper-accent font-medium">Узнаны по голосу:</span>
          <span className="flex-1 min-w-0 truncate text-paper-ink">{names}</span>
          <span aria-hidden="true" className="text-paper-ink-3">▾</span>
        </button>
        {closeButton}
      </div>
    );
  }

  return (
    <div className="kbd-hide relative flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 pr-9 mb-2 rounded-panel bg-paper-bg border text-sm">
      <span className="text-paper-accent font-medium">Узнаны по голосу:</span>
      {recognized.map((item) => {
        const status = state[item.speakerId] || (item.confirmed ? 'done' : undefined);
        return (
          <span key={item.speakerId} className="flex items-center gap-1.5">
            <span className="text-paper-ink">{item.name}</span>
            {status === 'done' ? (
              <span className="text-xs text-paper-success">запомнен</span>
            ) : status === 'rejected' ? (
              <span className="text-xs text-paper-ink-3">исправьте имя в окне участников</span>
            ) : status === 'saving' ? (
              <span className="text-xs text-paper-ink-3">сохраняем…</span>
            ) : status ? (
              <span className="text-xs text-paper-warning">не запомнен: {status}</span>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => confirm(item)}
                  className="px-2 py-0.5 rounded bg-paper-bg text-xs text-paper-accent hover:bg-paper-panel transition-colors"
                >
                  Это он
                </button>
                <button
                  type="button"
                  onClick={() => reject(item)}
                  className="px-2 py-0.5 rounded bg-paper-card text-xs text-paper-ink-2 hover:bg-paper-panel transition-colors"
                >
                  Не он
                </button>
              </>
            )}
          </span>
        );
      })}
      {suggested.map((item) => {
        const status = state[item.speakerId];
        if (status === 'declined') return null;
        return (
          <span key={`s-${item.speakerId}`} className="flex items-center gap-1.5 basis-full sm:basis-auto">
            <span className="text-paper-ink-3">Возможно, {item.label} — это</span>
            <span className="text-paper-ink">{item.name}</span>
            {status === 'done' ? (
              <span className="text-xs text-paper-success">запомнен</span>
            ) : status === 'saving' ? (
              <span className="text-xs text-paper-ink-3">сохраняем…</span>
            ) : status ? (
              <span className="text-xs text-paper-warning">не запомнен: {status}</span>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => accept(item)}
                  className="px-2 py-0.5 rounded bg-paper-bg text-xs text-paper-accent hover:bg-paper-panel transition-colors"
                >
                  Да, это он
                </button>
                <button
                  type="button"
                  onClick={() => declineSuggestion(item)}
                  className="px-2 py-0.5 rounded bg-paper-card text-xs text-paper-ink-2 hover:bg-paper-panel transition-colors"
                >
                  Нет
                </button>
              </>
            )}
          </span>
        );
      })}
      <span className="text-xs text-paper-ink-3 basis-full">
        Подтверждение добавляет запись голоса к отпечатку — в следующих встречах узнавание точнее.
      </span>
      {isMobile && (
        <button type="button" onClick={() => setExpanded(false)}
                className="text-xs text-paper-ink-2 underline underline-offset-[3px]">
          Свернуть
        </button>
      )}
      {closeButton}
    </div>
  );
};

export default VoiceIdBar;
